import { DispatchHelpMessage } from '@numeo/types'
import { KeyboardEvent, useState } from 'react'
import { Sparkles, TriangleAlert } from 'lucide-react'
import { IconButton, Tooltip } from 'components/ds'
import { SendButton } from 'components/ds/SendButton'
import { showWarning } from 'utils/snackbar'
import { useAuth } from 'hooks/auth'
import { useAppDispatch } from 'hooks'
import { sendDispatcherMessage } from '../services/dispatchHelpApi'
import { appendMessage, reconcileMessage, removeMessage, setAgentActivity } from '../store/dispatchHelpSlice'
import { scheduleReplyRefetch } from '../store/socket-handlers'

// Monotonic so two sends in the same millisecond don't collide on `local-${Date.now()}` (which
// appendMessage's id-dedupe would then silently drop).
let optimisticSeq = 0

interface MessageComposerProps {
    tabId: string
    canReply: boolean
    onRequestTakeover: () => void
    takingOver: boolean
}

// Dispatcher can address the AI with @numeoai — the message becomes a prompt for the agent
// (runs like a driver turn) instead of a note to the driver.
// Lookarounds keep an email like `contact@numeoai.io` from tripping the AI route (standalone token only).
const AI_MENTION = /(?<![\w.])@numeoai(?![\w.])/i
// Trailing "@partial" the dispatcher is currently typing (drives the mention suggestion).
const MENTION_TOKEN = /(^|\s)@(\w*)$/

export function MessageComposer({ tabId, canReply, onRequestTakeover }: MessageComposerProps) {
    const dispatch = useAppDispatch()
    const { user } = useAuth()
    const dispatcherName = user?.name || user?.email || 'Dispatcher'
    const [text, setText] = useState('')
    const [sending, setSending] = useState(false)

    const tokenMatch = text.match(MENTION_TOKEN)
    const showMention = !!tokenMatch && 'numeoai'.startsWith((tokenMatch[2] || '').toLowerCase()) && !AI_MENTION.test(text)
    const isAiPrompt = AI_MENTION.test(text)

    function insertMention() {
        setText((t) => t.replace(MENTION_TOKEN, '$1@numeoai '))
    }

    // One-tap AI: prefix the message with @numeoai so it routes to the agent (keeps @numeoai discoverable
    // now that the placeholder no longer spells it out). No-op if the message already addresses the AI.
    function askAi() {
        setText((t) => (AI_MENTION.test(t) ? t : `@numeoai ${t}`))
    }

    async function send() {
        const trimmed = text.trim()
        if (!trimmed || sending) return
        // Must claim the session before writing into it — nudge (with a one-tap Take over) instead of sending.
        if (!canReply) {
            showWarning('Take over the conversation before you can reply', undefined, { title: 'Take over', onClick: onRequestTakeover })
            return
        }
        const invokeAi = AI_MENTION.test(trimmed)
        setSending(true)
        setText('')
        const tempId = `local-${Date.now()}-${++optimisticSeq}`
        const optimistic: DispatchHelpMessage = {
            id: tempId,
            role: 'dispatcher',
            text: trimmed,
            authorName: dispatcherName,
            createdAt: new Date().toISOString(),
        }
        dispatch(appendMessage({ tabId, message: optimistic }))
        try {
            const saved = await sendDispatcherMessage(tabId, trimmed, dispatcherName, invokeAi)
            // Swap the optimistic placeholder for the persisted row (distinct ids → not a no-op).
            dispatch(reconcileMessage({ tabId, tempId, message: saved }))
            // Show that the AI took the prompt straight away — the pipeline beats replace this text as
            // the turn progresses, and the reply itself clears it. Refetch stays as the fallback path.
            if (invokeAi) {
                dispatch(setAgentActivity({ tabId, message: 'Working…' }))
                scheduleReplyRefetch(tabId, dispatch)
            }
        } catch {
            dispatch(removeMessage({ tabId, id: tempId })) // drop the un-sent optimistic bubble
            setText(trimmed) // restore the text so the operator can retry
        } finally {
            setSending(false)
        }
    }

    function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
        if (showMention && (e.key === 'Enter' || e.key === 'Tab')) {
            e.preventDefault()
            insertMention()
            return
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            void send()
        }
    }

    return (
        <div className="relative border-t border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
            {!canReply && (
                // Tappable amber bar — one tap claims the session so the operator can reply.
                <button
                    type="button"
                    onClick={onRequestTakeover}
                    className="flex w-full items-center justify-center gap-1.5 bg-amber-50 px-4 py-2 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:hover:bg-amber-500/20"
                >
                    <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
                    Take over the conversation to reply
                </button>
            )}
            {showMention && (
                <button
                    type="button"
                    onMouseDown={(e) => {
                        e.preventDefault()
                        insertMention()
                    }}
                    className="absolute bottom-full left-3 mb-1 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left shadow-lg dark:border-slate-600 dark:bg-slate-800"
                >
                    <Sparkles className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">@numeoai</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">Ask the AI</span>
                </button>
            )}
            {isAiPrompt && (
                <div className="flex items-center gap-1.5 px-3 pt-2 text-xs font-medium text-blue-600 dark:text-blue-400">
                    <Sparkles className="h-3.5 w-3.5" />
                    This message will be sent to the AI as a prompt
                </div>
            )}
            <div className="mx-auto flex w-full max-w-3xl items-end gap-1.5 px-3 py-2.5">
                {/* One-tap AI — keeps @numeoai discoverable; inserts the mention so the turn routes to the agent. */}
                <Tooltip title="Ask the AI (@numeoai)">
                    <IconButton variant="ghost" size="sm" icon={<Sparkles className="h-4 w-4 text-blue-500" />} onClick={askAi} aria-label="Ask the AI" />
                </Tooltip>
                <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={onKeyDown}
                    rows={1}
                    placeholder="Reply to the driver…"
                    className="max-h-32 min-h-[44px] flex-1 resize-none rounded-2xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:bg-slate-900"
                />
                <SendButton onClick={() => void send()} disabled={!text.trim() || sending} />
            </div>
        </div>
    )
}
